export const MENU_FLAT_CACHE_KEY = "menu_flat_v1";

const ENTITY_KEYS = [
  "courses",
  "categories",
  "products",
  "variants_categories",
  "variants",
  "ingredients",
  "tags",
  "limit_groups",
  "lists",
  "allergens",
];

const RELATION_KEYS = [
  "course_categories",
  "category_products",
  "product_allergens",
  "product_ingredients",
  "products_variants_categories",
  "categories_variants_categories",
  "variants_categories_variants",
  "variants_categories_products",
  "tags_categories",
  "tags_products",
  "products_limit_groups",
  "lists_limit_groups",
];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function buildById(rows = []) {
  return rows.reduce((acc, row) => {
    if (row?.id !== undefined && row?.id !== null) acc[row.id] = row;
    return acc;
  }, {});
}

function groupBy(rows = [], key) {
  return rows.reduce((acc, row) => {
    const groupKey = row?.[key];
    if (groupKey === undefined || groupKey === null) return acc;
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(row);
    return acc;
  }, {});
}

function buildCompositeIndex(rows = [], entityKey) {
  return rows.reduce((acc, row) => {
    const entityId = row?.[entityKey];
    const listId = row?.list_id ?? "base";
    if (entityId === undefined || entityId === null) return acc;
    acc[`${entityId}:${listId}`] = row;
    return acc;
  }, {});
}

function normalizeVariantCategoryIdKey(rows = []) {
  return rows.map((row) => {
    if (!row || typeof row !== "object") return row;
    if (row.variant_category_id !== undefined && row.variant_category_id !== null) return row;
    if (row.variants_category_id !== undefined && row.variants_category_id !== null) {
      return {
        ...row,
        variant_category_id: row.variants_category_id,
      };
    }
    return row;
  });
}

export function parseMenuFlatPayload(payload) {
  const data = payload?.data ?? payload;

  if (!data || typeof data !== "object") {
    throw new Error("Invalid menu flat payload");
  }

  if (data.version === undefined || data.version === null || data.version === "") {
    throw new Error("Missing menu flat data.version");
  }

  const entities = ENTITY_KEYS.reduce((acc, key) => {
    acc[key] = asArray(data[key]);
    return acc;
  }, {});

  const relations = RELATION_KEYS.reduce((acc, key) => {
    acc[key] = asArray(data?.relations?.[key]);
    return acc;
  }, {});

  relations.products_variants_categories = normalizeVariantCategoryIdKey(
    relations.products_variants_categories,
  );
  relations.categories_variants_categories = normalizeVariantCategoryIdKey(
    relations.categories_variants_categories,
  );
  relations.variants_categories_variants = normalizeVariantCategoryIdKey(
    relations.variants_categories_variants,
  );
  relations.variants_categories_products = normalizeVariantCategoryIdKey(
    relations.variants_categories_products,
  );

  const prices = {
    products: asArray(data?.prices?.products),
    variants: asArray(data?.prices?.variants),
  };

  const assortment = {
    products: asArray(data?.assortment?.products),
    variants: asArray(data?.assortment?.variants),
    ingredients: asArray(data?.assortment?.ingredients),
  };

  return {
    version: data.version,
    entities,
    relations,
    prices,
    assortment,
    byId: Object.keys(entities).reduce((acc, key) => {
      acc[key] = buildById(entities[key]);
      return acc;
    }, {}),
    relationIndex: {
      courseCategoriesByCourseId: groupBy(relations.course_categories, "course_id"),
      categoryProductsByCategoryId: groupBy(relations.category_products, "category_id"),
      productAllergensByProductId: groupBy(relations.product_allergens, "product_id"),
      productIngredientsByProductId: groupBy(relations.product_ingredients, "product_id"),
      productVariantCategoriesByProductId: groupBy(
        relations.products_variants_categories,
        "product_id",
      ),
      categoryVariantCategoriesByCategoryId: groupBy(
        relations.categories_variants_categories,
        "category_id",
      ),
      variantCategoryVariantsByVariantCategoryId: groupBy(
        relations.variants_categories_variants,
        "variant_category_id",
      ),
      variantCategoryProductsByVariantCategoryId: groupBy(
        relations.variants_categories_products,
        "variant_category_id",
      ),
      tagsCategoriesByCategoryId: groupBy(relations.tags_categories, "category_id"),
      tagsProductsByProductId: groupBy(relations.tags_products, "product_id"),
      productsLimitGroupsByProductId: groupBy(relations.products_limit_groups, "product_id"),
      listsLimitGroupsByLimitGroupId: groupBy(relations.lists_limit_groups, "limit_group_id"),
      categoryIdsByProductId: groupBy(
        relations.category_products.map((relation) => ({
          product_id: relation.product_id,
          category_id: relation.category_id,
        })),
        "product_id",
      ),
    },
    priceIndex: {
      products: buildCompositeIndex(prices.products, "product_id"),
      variants: buildCompositeIndex(prices.variants, "variant_id"),
    },
    assortmentIndex: {
      productsByProductId: groupBy(assortment.products, "product_id"),
      variantsByVariantId: groupBy(assortment.variants, "variant_id"),
      ingredientsByIngredientId: groupBy(assortment.ingredients, "ingredient_id"),
    },
    priceGroups: {
      productsByProductId: groupBy(prices.products, "product_id"),
      variantsByVariantId: groupBy(prices.variants, "variant_id"),
    },
  };
}
